import {
    IReturnLabelSettings,
    IShipmentOptionsForSelectionResponseDto,
    LABEL_SETTING_AUTO_SELECTION_TYPE,
    returnLabelSettingAutoSelectionTypeOptions
} from "../return-method-types";
import {IOption} from "../../../components/inputs/select-dropdown/select-dropdown";
import {
    IOption as INestedSelectorOption
} from "../components/return-method-editor/multi-nested-selector/multi-nested-selector";
import ReturnMethodsService from "../../../services/api/return-methods-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import {makeAutoObservable, runInAction} from "mobx";
import {SingleValue} from "react-select";
import {IChangeable} from "../../../stores/types";
import ReturnLabelSettingCarrierStore from "./return-label-setting-carrier-store";
import {CarrierOptionsCacheStore} from "./carrier-options-cache-store";
import {getNullableChangesOrModel} from "../../../models/model-types-methods";
import { ICheckboxesTreeItem } from "../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";

interface IReturnLabelSettingsChange extends Partial<IReturnLabelSettings> {
}

export default class CarrierSelectionOptionsStore implements IChangeable<IReturnLabelSettings> {

    private _changes: IReturnLabelSettingsChange = {};
    private _isLoading: boolean = false;
    private readonly _autoSelectionTypeOptions: IOption[] = returnLabelSettingAutoSelectionTypeOptions();
    private _carriersStores: { [carrierCode: string]: ReturnLabelSettingCarrierStore } = {};
    /** when drop down has no selected carriers, all unchecked, initial value False, e.g. we assume empty array from server always means all carriers are selectedm server should not allow saving NULL carriers */
    private _isEmptyCarrierSelection: boolean = false; 

    constructor(
        private _getModel: () => IReturnLabelSettings | undefined,
        private getShippingConnectionId: () => number,
        private carrierOptionsCacheStore: CarrierOptionsCacheStore
    ) {
        makeAutoObservable(this, {}, {autoBind: true});

        this._getModel()?.return_label_setting_carriers?.forEach(
            (carrier) => this._carriersStores[carrier.carrier_code] = new ReturnLabelSettingCarrierStore(carrier)
        );
    }

    private get returnMethodsService(): ReturnMethodsService {
        return rootContainer.get<ReturnMethodsService>(TYPE.ReturnMethodsService);
    }

    public async load() {
        if(!this.getShippingConnectionId()) {
            return ;
        }

        this._isLoading = true;

        try {
            const connectionId = this.getShippingConnectionId();
            const fetchedCarriersOptionsList = await (this.returnMethodsService.getCarrierSelectionOptionsStore(connectionId));
            runInAction(() => {
                this.carrierOptionsCacheStore.setCarriersAndServicesOptions(connectionId, transformShipmentDtoToMultiSelectorOptions(fetchedCarriersOptionsList));
                this._isLoading = false;
            });

        } catch {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public getAutoSelectionTypeOptions(): IOption[] {
        return this._autoSelectionTypeOptions;
    }

    /** List of all possible multi-select options*/
    public getCarrierSelectionOptions(): ICheckboxesTreeItem[] {
        return this.carrierOptionsCacheStore.getCarriersAndServicesOptions(this.getShippingConnectionId());
    }

    public setCarriers(selectedOptions: ICheckboxesTreeItem[]|undefined): void {
        if (selectedOptions === undefined) {
            Object.values(this._carriersStores).forEach(store => store.setDeleted(true));
            this._isEmptyCarrierSelection = true;
            return;
        }

        this._isEmptyCarrierSelection = false;
        const selectedCarrierCodes: { [carrierCode: string]: boolean } = {};

        selectedOptions.forEach(carrierOp => {
            let carrierStore = this._carriersStores[carrierOp.value];
            if (!carrierStore) {
                /** creating new store and register in this._carriersStores */
                carrierStore = new ReturnLabelSettingCarrierStore();
                this._carriersStores[carrierOp.value] = carrierStore;
            }
            carrierStore.setCarrierOption(carrierOp);
            selectedCarrierCodes[carrierOp.value] = true;
            carrierStore.setDeleted(false);
        });

        Object.keys(this._carriersStores).forEach(carrierCode => {
            if (!selectedCarrierCodes[carrierCode]) {
                this._carriersStores[carrierCode].setDeleted(true);
            }
        });
    }

    public async setDefaultValues() {
        runInAction(() => {

            this.setCarriers([]); /** setting an empty array - ALL selected */

            /** By default, cheapest auto-selection type should be checked */
            if(!this.autoSelectionType) {
                this._changes.auto_selection_type = LABEL_SETTING_AUTO_SELECTION_TYPE.CHEAPEST;
            }
        });
    }

    public getSelectedCarriers(): INestedSelectorOption[] | undefined {
        if (this._isEmptyCarrierSelection) {
            return undefined;
        }

        const carrierCodes: string[] = this.getCarrierSelectionOptions().map(carrierOp => carrierOp.value);

        return Object.values(this._carriersStores)
            .filter(store => !store.deleted())

            /** Filter by available carrier codes received from api*/
            .filter(store => carrierCodes.includes(store.getCarrierOption().value))
            .map(store => store.getCarrierOption());
    }

    public hasChanges(): boolean {
        const hasCarriersChanges = Object.values(this._carriersStores).some(store => store.hasChanges());
        /** we need to check _isEmptyCarrierSelection because we might receive empty array of carriers from server and unchecking them all will NOT add any changes to stores */
        return hasCarriersChanges || !!Object.keys(this._changes).length || this._isEmptyCarrierSelection;
    }

    discardChanges(): void {
        Object.values(this._carriersStores).forEach(store => store.discardChanges());
        this._changes = {};
        this._isEmptyCarrierSelection = false;
    }

    public isLoading() {
        return this._isLoading;
    }

    private get autoSelectionType(): LABEL_SETTING_AUTO_SELECTION_TYPE|null|undefined {
        return getNullableChangesOrModel(this._changes.auto_selection_type, this._getModel()?.auto_selection_type);
    }

    public getAutoSelectionTypeSelectedValue(): IOption | undefined {
        const selectionType = this.autoSelectionType;
        return this.getAutoSelectionTypeOptions().find(autoSelectionTypeOption => autoSelectionTypeOption.value === selectionType);
    }

    public getUpdatedModel() {
        const returnLabelSettingCarriers = this._isEmptyCarrierSelection 
            ? null 
            : Object.values(this._carriersStores).filter(store => !store.isGhost()).map(store => store.getUpdatedModel());

        return {
            auto_selection_type: this.autoSelectionType,
            return_label_setting_carriers: returnLabelSettingCarriers,
        } as IReturnLabelSettings
    }

    public setAutoSelectionTypeValue(v: SingleValue<IOption>) {
        this._changes.auto_selection_type = v?.value as LABEL_SETTING_AUTO_SELECTION_TYPE;
    }

    public resetSelections(): void {
        this._changes.auto_selection_type = null;
        Object.values(this._carriersStores).forEach(store => store.setDeleted(true));
        this._isEmptyCarrierSelection = true; /** reseting - e.g. deleting all previous selections when connection changes before setting defaults */
    }
}


/** Responsible for correct mapping from DTO to multi-select options */
function transformShipmentDtoToMultiSelectorOptions(carriersOptionsList: IShipmentOptionsForSelectionResponseDto): ICheckboxesTreeItem[] {
    return carriersOptionsList.list.map((carriersForSelection) => (
            {
                label: carriersForSelection.carrierName,
                value: carriersForSelection.carrierCode,
                childNodes: carriersForSelection.list.map((serviceForSelection) => ({
                        label: serviceForSelection.serviceName,
                        value: serviceForSelection.serviceCode
                    })
                )
            }
        )
    ).sort((optionA: ICheckboxesTreeItem, optionB: ICheckboxesTreeItem) => optionA.label.localeCompare(optionB.label));
}
